import { useEffect, useState } from "react";
import BaseModal from "./BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Icon1 from "@assets/icons/Icon";
import { serviceboxSyncApi, type OrdreSyncHistory } from "@services/api/serviceboxSync";
import { ordresApi, type RdvMove, type RdvMoveSlot } from "@services/api/ordres";
import { useDataStore } from "@store/user_org";
import type { Ordre } from "types/ordre";

/**
 * Where this ordre came from, and every ServiceBox exchange that is actually recorded.
 *
 * Two things the garage keeps needing on a rendez-vous that looks wrong: was it created in
 * ServiceBox or in Carvis, and what has been written between the two since. The answer is
 * built only from stored fields — each line names the one it came from — and the modal
 * shows what the timeline CANNOT see rather than letting silence read as "nothing happened".
 */
function frDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("fr-FR", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function hourLabel(h: number): string {
  const hh = Math.floor(h + 1e-9);
  const mm = Math.floor((h - hh) * 60 + 1e-9);
  return `${String(hh).padStart(2, "0")}h${String(mm).padStart(2, "0")}`;
}

const MOVE_ACTION: Record<string, string> = { create: "Créé", move: "Déplacé", delete: "Supprimé" };
const LEG: Record<string, string> = { reception: "Réception", restitution: "Restitution" };

const OUTCOME: Record<string, { color: string; icon: string; label: string }> = {
  ok: { color: "#059669", icon: "done2", label: "Réussi" },
  ko: { color: "#DC2626", icon: "close", label: "Échoué" },
  unknown: { color: "#9CA3AF", icon: "minus", label: "En attente" },
};

export default function OrdreSyncHistoryModal({
  ordre,
  onClose,
}: {
  ordre: Ordre;
  onClose: () => void;
}) {
  const [data, setData] = useState<OrdreSyncHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [moves, setMoves] = useState<RdvMove[] | null>(null);
  const receptions = useDataStore((s) => s.orgData?.receptions);

  const slotLabel = (slot?: RdvMoveSlot | null) => {
    if (!slot) return "—";
    const [y, m, d] = (slot.date || "").split("-");
    const desk = receptions?.find((r) => r.id === slot.category)?.label || slot.category || "";
    return `${d}/${m}/${y} ${hourLabel(slot.start_hour || 0)}${desk ? ` · ${desk}` : ""}`;
  };

  // Déplacements du RDV : indépendant de ServiceBox, chargé à part pour qu'un échec
  // de l'un n'efface pas l'autre.
  useEffect(() => {
    let cancelled = false;
    ordresApi
      .rdvMoves(ordre.id)
      .then((m) => !cancelled && setMoves(m))
      .catch(() => !cancelled && setMoves([]));
    return () => {
      cancelled = true;
    };
  }, [ordre.id]);

  useEffect(() => {
    let cancelled = false;
    serviceboxSyncApi
      .getSyncHistory(ordre.id)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        if (!d.success) setError(d.error || "Historique indisponible");
      })
      .catch((e) => !cancelled && setError(e?.response?.data?.detail || e?.message || "Erreur"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [ordre.id]);

  const origin = data?.origin;
  const fromServiceBox = origin?.where === "servicebox";
  const ids = Object.entries(data?.identifiers || {}).filter(([, v]) => v);

  return (
    <BaseModal
      onClose={onClose}
      title="Historique du RDV"
      startIcon={<Icon1 name="sett-servicebox" size={20} />}
      width="max-w-2xl"
    >
      <div className="flex flex-col gap-4 p-4">
        {/* Qui a créé / déplacé / supprimé la réception et la restitution, et par où. */}
        <div>
          <div className="text-h3 font-medium mb-1">Déplacements du RDV</div>
          {moves === null ? (
            <div className="text-h3 text-neutral-400">Chargement…</div>
          ) : moves.length ? (
            <div className="flex flex-col gap-2">
              {moves.map((m) => (
                <div key={m.id} className="flex flex-col">
                  <div className="text-h3 text-neutral-800">
                    {MOVE_ACTION[m.action] || m.action} · {LEG[m.type || ""] || m.type}
                    {m.action === "move" ? (
                      <>
                        {" : "}
                        <span className="text-neutral-500">{slotLabel(m.from)}</span>
                        {" → "}
                        <span className="font-medium">{slotLabel(m.to)}</span>
                      </>
                    ) : (
                      <span className="text-neutral-500"> : {slotLabel(m.to || m.from)}</span>
                    )}
                  </div>
                  <div className="text-h4 text-neutral-500">
                    {frDate(m.at)} · {m.source_label || m.source}
                    {m.user_name ? ` · ${m.user_name}` : ""}
                  </div>
                  {m.note && <div className="text-h4 text-orange-600">{m.note}</div>}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-h3 text-neutral-400">
              Aucun déplacement enregistré (historique tenu depuis le 08/10/2026).
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex flex-row items-center gap-3 p-4">
            <div className="w-5 h-5 rounded-full border-[3px] border-neutral-200 border-t-neutral-700 animate-spin" />
            <div className="text-h3 text-neutral-500">Lecture de l'historique…</div>
          </div>
        ) : error ? (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-h3">{error}</div>
        ) : (
          <>
            {/* Origin — the first question, answered in one line with its evidence. */}
            {origin && (
              <div
                className={`p-3 rounded-lg border text-h3 ${
                  fromServiceBox
                    ? "bg-blue-50 border-blue-200 text-blue-900"
                    : "bg-violet-50 border-violet-200 text-violet-900"
                }`}
              >
                <div className="font-medium">{origin.label}</div>
                <div className="text-h4 opacity-80 mt-0.5">
                  {frDate(origin.at)}
                  {origin.evidence?.import_source ? ` · source : ${origin.evidence.import_source}` : ""}
                </div>
              </div>
            )}

            {ids.length > 0 && (
              <div className="flex flex-row flex-wrap gap-x-4 gap-y-1 text-h4 text-neutral-500">
                {ids.map(([k, v]) => (
                  <span key={k}>
                    {k} : <span className="text-neutral-700 font-medium">{v}</span>
                  </span>
                ))}
              </div>
            )}

            {/* Carvis vs the last value ServiceBox showed. A row that disagrees is what the
                next sync will act on; a row that agrees can still agree on a wrong value. */}
            {data?.state?.length ? (
              <div>
                <div className="text-h3 font-medium mb-1">Carvis / ServiceBox</div>
                <div className="flex flex-col gap-1">
                  {data.state.map((row) => (
                    <div key={row.field} className="flex flex-row items-baseline gap-2 text-h3">
                      <Icon1
                        name={row.in_sync ? "done2" : "warning"}
                        size={14}
                        color={row.in_sync ? "#059669" : "#EA580C"}
                      />
                      <span className="w-160 shrink-0 text-neutral-500">{row.field}</span>
                      <span className="flex-1 text-neutral-800">{row.carvis || "—"}</span>
                      <span className="flex-1 text-neutral-500">{row.servicebox || "—"}</span>
                      {row.no_baseline && (
                        <span className="text-h4 text-neutral-400">aucune référence SB</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {/* The trail itself. */}
            <div>
              <div className="text-h3 font-medium mb-1">Échanges enregistrés</div>
              {data?.events?.length ? (
                <div className="flex flex-col gap-2">
                  {data.events.map((ev, i) => {
                    const o = OUTCOME[ev.success === true ? "ok" : ev.success === false ? "ko" : "unknown"];
                    return (
                      <div key={`${ev.source}-${i}`} className="flex flex-row items-start gap-2">
                        <Icon1 name={o.icon} size={14} color={o.color} />
                        <div className="flex-1">
                          <div className="text-h3 text-neutral-800">
                            {ev.title}
                            {ev.attempt && ev.attempt > 1 ? (
                              <span className="text-neutral-400"> (tentative {ev.attempt})</span>
                            ) : null}
                          </div>
                          <div className="text-h4 text-neutral-500">
                            {frDate(ev.at)}
                            {ev.detail ? ` · ${ev.detail}` : ""}
                          </div>
                          {ev.error && <div className="text-h4 text-red-600">{ev.error}</div>}
                          {/* The field this line was read from: nothing here is inferred. */}
                          <div className="text-h4 text-neutral-300">{ev.source}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-h3 text-neutral-400">Aucun échange enregistré.</div>
              )}
            </div>

            {/* What this timeline cannot see — said, not hidden. */}
            {data?.gaps?.length ? (
              <div className="p-3 rounded-lg bg-neutral-50 border border-neutral-200 text-h4 text-neutral-600">
                <div className="font-medium mb-0.5">Ce que cet historique ne voit pas</div>
                <ul className="list-disc pl-4 flex flex-col gap-0.5">
                  {data.gaps.map((g) => (
                    <li key={g}>{g}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}

        <div className="flex flex-row justify-end">
          <SecondaryButton label="Fermer" onClick={onClose} />
        </div>
      </div>
    </BaseModal>
  );
}
